import React, { useEffect, useState } from 'react';
import { get } from 'lodash-es';
import {
  Alert,
  Col,
  Row,
  Container,
  Button,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip
} from 'reactstrap';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TaxType from './TaxType';
import FeeType from './FeeType';
import Messages from './Messages';
import { useAxios } from '../../../hooks';
import { selectHouseRef, selectIsInvShippingEnabled } from '../../../selectors';
import ReadOnlyTemplate from './ReadOnlyTemplate';
import DummyTaxTemplate from './DummyTaxTemplate';
import { invoicesPath } from '../../../utilities/UrlPaths';

const InvoiceTemplates = ({ ...props }) => {
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [dropDownTemplates, setDropDownTemplates] = useState([]);
  const [selectedTemplateLabel, setSelectedTemplateLabel] = useState('');
  const [taxData, setTaxData] = useState([]);
  const [feeData, setFeeData] = useState([]);
  const [messageData, setMessageData] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [chargeByLot, setChargeByLot] = useState(false);
  const [chargeByInvoice, setChargeByInvoice] = useState(false);
  const [validation, setValidation] = useState({});
  const [newTemplate, setNewTemplate] = useState(false);
  const [readOnlyTemplate, setReadOnlyTemplate] = useState(false);
  const [payrixParentTemplateFlag, setPayrixParentTemplateFlag] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    emailSubject: '',
    replyName: '',
    replyEmail: '',
    BCC: '',
    HouseAddr: []
  });
  const [disabledButton, setDisabledButton] = useState(false);
  const [paypalEmailValue, setPaypalEmailValue] = useState('');

  const houseRef = useSelector(selectHouseRef);
  const isInvShippingEnabled = useSelector(selectIsInvShippingEnabled);
  const houseData = useSelector(state => state.house);
  const processorType = useSelector(state => state.invoice.processorType);
  const paymentProcessingEnabled = useSelector(state => state.user.paymentProcessingEnabled);
  const history = useHistory();
  const { settingsID } = useParams();

  const isPayrix = processorType === 'p' && paymentProcessingEnabled;

  const defaultMessages = [
    {
      detail: ' ',
      header: 'Payment Option(s)',
      sequence: 1
    },
    {
      detail: ' ',
      header: 'Shipping Notes',
      sequence: 2
    }
  ];

  const {
    isIdle,
    isLoading,
    data: invoiceGroupSettingsData,
    exec: execInvoiceGroupSettings
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroupSettings?groupSettingsID=${settingsID}`,
    [houseRef],
    { method: 'get' },
    { manual: true }
  );

  const handleCreateNewTemplate = async () => {
    await props.history.push('/invoices/invoiceTemplates');
    setValidation({});
    setTemplateName('');
    setNewTemplate(true);
    setReadOnlyTemplate(false);
    setEmailSettings({
      emailSubject: '',
      replyName: '',
      replyEmail: '',
      BCC: '',
      selectedAddr: houseData.houseAddress,
      HouseAddr: houseData.houseAddresses
    });
    setChargeByInvoice(isInvShippingEnabled ? false : true);
    setChargeByLot(isInvShippingEnabled ? true : false);
    setMessageData(defaultMessages);
    handleDeleteTaxType('new');
    handleDeleteFeeType('new');
  };

  const { data: invoiceSettingsData, exec: execInvoiceSettings } = useAxios(
    () => `/app/house/${houseRef}/invoiceSettings`,
    [houseRef],
    { method: 'get' },
    { manual: true }
  );

  const {
    isSuccess,
    data: invoiceSettingsResponseData,
    execAsync: execAsyncGroupSettings
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroupSettings`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  useEffect(() => {
    setValidation({});
    execInvoiceGroupSettings();
    execInvoiceSettings();
  }, [execInvoiceGroupSettings, execInvoiceSettings]);

  useEffect(() => {
    if (isSuccess) {
      setIsSavingTemplate(false);
      setNewTemplate(false);
      setMessageData(messageData);
      setTemplateSaved(true);
      setTaxData(taxData);
      setFeeData(feeData);
      window.location.href = `${invoicesPath}/invoiceTemplates/${invoiceSettingsResponseData.data.data.groupSettingsID}`;
    }
  }, [isSuccess]);

  useEffect(() => {
    if (typeof invoiceSettingsData === 'undefined') {
      return;
    }

    if (!templateName) {
      setSelectedTemplateLabel('Select Existing Template');
      setReadOnlyTemplate(false);
    }

    setDropDownTemplates(get(invoiceSettingsData, 'data.data.invoiceSettings', []));
  }, [invoiceSettingsData]);

  useEffect(() => {
    if (typeof invoiceGroupSettingsData === 'undefined') {
      return;
    }

    const templateData = get(invoiceGroupSettingsData, 'data.data.groupSettings', []);
    const payrixTemplateFlag = get(
      invoiceGroupSettingsData,
      'data.data.groupSettings.payrixTemplate',
      []
    );
    const invoicDataArray = get(invoiceSettingsData, 'data.data.invoiceSettings', []);

    if (typeof templateData === 'object' && !Array.isArray(templateData)) {
      const currentAddress = houseData.houseAddresses.filter(
        address => address.addressID === templateData.houseAddressID
      );
      setPayrixParentTemplateFlag(payrixTemplateFlag);
      setTemplateSaved(false);
      setNewTemplate(false);
      setTemplateName(templateData.groupName);
      setSelectedTemplateLabel(templateData.groupName);
      setChargeByLot(templateData.additionalChargesPerLot);
      setChargeByInvoice(templateData.additionalChargesPerInvoice);
      setPaypalEmailValue(templateData.payPalEmail);

      if (templateData.taxSettings) {
        setTaxData(templateData.taxSettings);
      }
      if (templateData.feeSettings) {
        setFeeData(templateData.feeSettings);
      }
      if (templateData.messages) {
        setMessageData(templateData.messages);
      }
      setEmailSettings({
        emailSubject: templateData.subject || '',
        replyName: templateData.fromName || '',
        replyEmail: templateData.fromEmail || '',
        BCC: templateData.bccEmail || '',
        selectedAddr: currentAddress[0],
        HouseAddr: houseData.houseAddresses
      });

      const invoiceGroupId = templateData.invoiceGroupID;
      let payrixTemplateSelected;

      if (invoiceGroupId) {
        invoicDataArray.forEach(invoice => {
          if (invoice.id === invoiceGroupId) {
            payrixTemplateSelected = invoice.payrixTemplate;
          }
        });
      }

      // check if processor type is payrix house
      // if the payrix template type is false show read only
      if (processorType === 'p' && !payrixTemplateSelected) {
        setReadOnlyTemplate(true);
      } else {
        setReadOnlyTemplate(false);
      }
      setIsSavingTemplate(false);
    } else {
      setChargeByInvoice(true);
      setEmailSettings({
        emailSubject: '',
        replyName: '',
        replyEmail: '',
        BCC: '',
        selectedAddr: houseData.houseAddress,
        HouseAddr: houseData.houseAddresses
      });
      setMessageData(defaultMessages);
    }
  }, [invoiceGroupSettingsData, houseData, processorType, settingsID]);

  const handleTemplateSelection = (templateId, templateName) => {
    if (templateId === '') {
      window.location.href = `${invoicesPath}/invoiceTemplates/`;
    } else {
      window.location.href = `${invoicesPath}/invoiceTemplates/${templateId}`;
    }
  };

  const handleEmailSettingChange = (index, value) => {
    setEmailSettings({
      ...emailSettings,
      [index]: value
    });
  };

  const handleValidation = () => {
    let allFieldsPass = false;
    const newValidationObj = {};

    // Email settings validation
    if (templateName.length < 1) {
      newValidationObj.templateName = 'Invoice template name is required';
    }

    if (emailSettings.emailSubject.length < 1) {
      newValidationObj.emailSubject = 'Email Subject is required';
    }
    if (emailSettings.replyName.length < 1) {
      newValidationObj.replyName = 'Reply to Name is required';
    }
    if (emailSettings.replyEmail.length < 1) {
      newValidationObj.replyEmail = 'Reply to Email is required';
    } else if (!/(.+)@(.+){2,}\.(.+){2,}/.test(emailSettings.replyEmail)) {
      // invalid email
      newValidationObj.replyEmail = 'Valid Email is required';
    }

    // BCC is not required but if present do basic validation
    if (emailSettings.BCC.length > 0) {
      if (!/(.+)@(.+){2,}\.(.+){2,}/.test(emailSettings.BCC)) {
        // invalid email
        newValidationObj.BCCError = 'Valid Email is required';
      }
    }

    // tax settings validation
    if (taxData) {
      const taxErrors = [];
      taxData.forEach((taxCharge, index) => {
        let taxValidObj = {};
        if (!taxCharge.taxCountryID && !taxCharge.taxCountry) {
          taxValidObj.errorCountry = true;
          taxValidObj.errorIndex = index;
        }

        if (!taxCharge.taxRate) {
          taxValidObj.errorTaxRate = true;
          taxValidObj.errorIndex = index;
        } else if (typeof number === taxCharge.taxRate) {
          taxValidObj.errorTaxRate = true;
          taxValidObj.errorIndex = index;
        }

        if (!taxCharge.taxType) {
          taxValidObj.errorTaxType = true;
          taxValidObj.errorIndex = index;
        }
        if (
          (taxCharge.taxCountryID === 'US' || taxCharge.taxCountry === 'US') &&
          !taxCharge.taxStateID
        ) {
          taxValidObj.errorState = true;
          taxValidObj.errorIndex = index;
        }

        if (!taxCharge.taxHammer && !taxCharge.taxLiveFee && !taxCharge.taxPremium) {
          taxValidObj.checkBoxError = true;
          taxValidObj.errorIndex = index;
        }
        if (
          taxCharge.taxCountryID === '' &&
          taxCharge.taxRate === '' &&
          taxCharge.taxType === '' &&
          taxCharge.taxHammer === '' &&
          taxCharge.taxLiveFee === '' &&
          taxCharge.taxPremium === ''
        ) {
          taxValidObj = {};
        }
        if (Object.keys(taxValidObj).length !== 0) {
          taxErrors.push(taxValidObj);
        }
      });

      if (taxErrors.length > 0) {
        newValidationObj.taxErrors = taxErrors;
      }
    }

    // fee data validation
    if (feeData) {
      const feeErrors = [];
      feeData.forEach((feeCharge, index) => {
        const feeValidObj = {};
        if (!feeCharge.feeCountryID && !feeCharge.feeCountry) {
          feeValidObj.errorCountry = true;
          feeValidObj.errorIndex = index;
        }
        if (!feeCharge.feeRate) {
          feeValidObj.errorFeeRate = true;
          feeValidObj.errorIndex = index;
        } else if (typeof number === feeCharge.feeRate) {
          feeValidObj.errorFeeRate = true;
          feeValidObj.errorIndex = index;
        }

        if (!feeCharge.feeType) {
          feeValidObj.errorFeeType = true;
          feeValidObj.errorIndex = index;
        }
        if (
          (feeCharge.feeCountryID === 'US' || feeCharge.feeCountry === 'US') &&
          !feeCharge.feeStateID
        ) {
          feeValidObj.errorState = true;
          feeValidObj.errorIndex = index;
        }

        if (!feeCharge.feeHammer && !feeCharge.feeLiveFee && !feeCharge.feePremium) {
          feeValidObj.checkBoxError = true;
          feeValidObj.errorIndex = index;
        }

        if (Object.keys(feeValidObj).length !== 0) {
          feeErrors.push(feeValidObj);
        }
      });

      if (feeErrors.length > 0) {
        newValidationObj.feeErrors = feeErrors;
      }
    }

    // Message Data

    if (messageData) {
      const messageErrors = [];
      const sequenceVals = [];
      messageData.forEach(message => {
        sequenceVals.push(message.sequence);
      });

      const duplicatesArr = sequenceVals.filter((e, i, a) => a.indexOf(e) !== i);

      if (duplicatesArr.length > 0) {
        duplicatesArr.forEach(duplicate => {
          messageErrors.push({ sequenceError: duplicate });
        });
      }

      if (messageErrors.length > 0) {
        newValidationObj.messageErrors = messageErrors;
      }
    }

    console.log('error object', newValidationObj);

    // set our validation object so errors appear in fields
    setValidation(newValidationObj);

    if (Object.keys(newValidationObj).length !== 0) {
      newValidationObj.genericError = 'Please correct the errors noted and try saving again.';
    }

    if (Object.keys(newValidationObj).length === 0) {
      allFieldsPass = true;
    }

    return allFieldsPass;
  };

  const handleEmptyMessageOnSave = () => {
    const messageHeaderArr = messageData.filter(message => message.header !== '');
    setMessageData(messageHeaderArr);
    return messageHeaderArr;
  };

  const handleSaveChanges = () => {
    if (handleValidation()) {
      setValidation({});
      setIsSavingTemplate(true);
      setSelectedTemplateLabel(templateName);
      execAsyncGroupSettings({
        data: {
          additionalChargesPerInvoice: chargeByInvoice,
          additionalChargesPerLot: chargeByLot,
          bccEmail: emailSettings.BCC,
          fromEmail: emailSettings.replyEmail,
          fromName: emailSettings.replyName,
          groupName: templateName,
          houseAddressID: emailSettings.selectedAddr.id,
          invoiceGroupID: settingsID || 0,
          messages: handleEmptyMessageOnSave(),
          payPal: paypalEmailValue !== '',
          payPalEmail: paypalEmailValue,
          subject: emailSettings.emailSubject,
          taxSettings: taxData,
          feeSettings: feeData
        },
        headers: { Authorization: `${houseRef}*` }
      });
    } else {
      setIsSavingTemplate(false);
    }
  };

  const handleNewMessage = () => {
    setMessageData(prev => [
      ...prev,
      {
        detail: '',
        header: '',
        sequence: messageData.length + 1
      }
    ]);
  };

  const handleNewTaxChargeType = () => {
    setTaxData([
      ...taxData,
      {
        formattedTaxRate: '',
        taxCountry: '',
        taxCountryID: '',
        taxHammer: '',
        taxLiveFee: '',
        taxPremium: '',
        taxRate: '',
        taxStateID: '',
        taxType: ''
      }
    ]);
  };

  const handleDeleteTaxType = index => {
    if (index === 'new') {
      setTaxData(() => []);
    } else {
      setTaxData(prevState => {
        const newTaxDataArr = Array.from(prevState);
        newTaxDataArr.splice(index, 1);
        return newTaxDataArr;
      });
    }
  };

  const handleNewFeeChargeType = () => {
    setFeeData([
      ...feeData,
      {
        feeCountry: '',
        feeCountryID: '',
        feeHammer: '',
        feeLiveFee: '',
        feePremium: '',
        feeRate: '',
        feeStateID: '',
        feeType: '',
        formattedFeeRate: ''
      }
    ]);
  };

  const handleDeleteFeeType = index => {
    if(index === 'new'){
      setFeeData(() => []);
    } else {
      setFeeData((prevState) => {
        const newFeeDataArr = Array.from(prevState);
        newFeeDataArr.splice(index, 1);
        return newFeeDataArr;
      });
    }
  };

  const handleBillingType = e => {
    const { checked, id } = e.target;
    if (id === 'byInvoice' && checked === true) {
      setChargeByInvoice(true);
      setChargeByLot(false);
    } else {
      setChargeByInvoice(false);
      setChargeByLot(true);
    }
  };

  const toggleSaveDisabled = flag => {
    setDisabledButton(flag);
  };

  if ((isIdle || isLoading) && settingsID) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  return (
    <Container className="mb-5">
      {processorType === 'p' && readOnlyTemplate && !payrixParentTemplateFlag && (
        <Row className="mt-4">
          <Col>
            <Alert color="info">
              <FontAwesomeIcon icon="question-circle" id="readOnly" />
              &nbsp; This template is no longer active. You may view it, but it can not be edited or
              used for any new auctions. Any existing invoices using this template must be charged
              manually.
            </Alert>
          </Col>
        </Row>
      )}
      <Row className={`${readOnlyTemplate ? 'mt-2' : 'mt-4'}`}>
        <Col xs={12}>
          {/* <FontAwesomeIcon icon="" className="fa-solid color-white-bg" />  */}
          <h1 className="">Invoice Templates</h1>
        </Col>
      </Row>
      <Row className="mt-5">
        {!newTemplate && (
          <Col xs={4}>
            <h3>Invoice Templates</h3>
            <UncontrolledDropdown className="w-100">
              <DropdownToggle caret color="white" className="text-truncate small-important">
                {selectedTemplateLabel}
              </DropdownToggle>
              <DropdownMenu className="full-width-dropdown">
                <DropdownItem key={0} className="w-100 small-important" />

                {dropDownTemplates &&
                  dropDownTemplates.map(template => (
                    <DropdownItem
                      key={template.id}
                      className="w-100 text-clamp-2 small-important"
                      onClick={() => handleTemplateSelection(template.id, template.name)}
                    >
                      {template.name}
                    </DropdownItem>
                  ))}
              </DropdownMenu>
            </UncontrolledDropdown>
            <Button color="link" aria-label="create new " onClick={handleCreateNewTemplate}>
              + Create New Template
            </Button>
          </Col>
        )}
        <Col xs={4}>
          <FormGroup>
            <h3>Invoice Template Name</h3>
            {readOnlyTemplate && processorType === 'p' ? (
              <>{templateName}</>
            ) : (
              <Input
                id="templateName"
                className=""
                maxLength={50}
                name="templateName"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
              />
            )}
            {validation.genericError && (
              <div className="control-label error-message mt-1" htmlFor="templateName">
                {validation.templateName}
              </div>
            )}
          </FormGroup>
        </Col>
        <Col xs={newTemplate ? 8 : 4} className="align-right">
          {newTemplate && (
            <Button
              className="mr-3"
              color="link"
              aria-label="create new "
              onClick={() => {
                setNewTemplate(false);
                setReadOnlyTemplate(false);
                setSelectedTemplateLabel('Select Existing Template');
                setTemplateName('');
                setValidation({});
              }}
            >
              Cancel
            </Button>
          )}
          {!readOnlyTemplate && (
            <Button
              color="primary"
              aria-label="Save"
              onClick={handleSaveChanges}
              disabled={isSavingTemplate}
            >
              Save Changes
            </Button>
          )}
          {validation.genericError && (
            <div className="control-label error-message mt-1" htmlFor="emailSubject">
              {validation.genericError}
            </div>
          )}
          {templateSaved && <div className="control-label mt-1">Your changes have been saved.</div>}
        </Col>
      </Row>

      {readOnlyTemplate && processorType === 'p' && !newTemplate ? (
        <ReadOnlyTemplate
          emailData={emailSettings}
          taxData={taxData}
          feeData={feeData}
          messageData={messageData}
          chargeByInvoice={chargeByInvoice}
          chargeByLot={chargeByLot}
        />
      ) : (
        <>
          <Row className="mt-5">
            <Col xs={12}>
              <Alert color="primary">Email Settings</Alert>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={5} className="mt-3">
              <FormGroup className={`${validation.emailSubject ? 'has-error' : ''}`}>
                <h3>
                  Email Subject&nbsp;
                  <FontAwesomeIcon icon="question-circle" id="emailSubjectToolTip" />
                  <UncontrolledTooltip placement="bottom" target="emailSubjectToolTip">
                    This is the subject line for your invoice emails.
                  </UncontrolledTooltip>
                </h3>
                <Input
                  id="emailSubject"
                  className=""
                  maxLength={72}
                  name="emailSubject"
                  value={emailSettings.emailSubject}
                  onChange={e => handleEmailSettingChange('emailSubject', e.target.value)}
                />
                {validation.emailSubject && (
                  <label className="control-label error-message" htmlFor="emailSubject">
                    {validation.emailSubject}
                  </label>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={5}>
              <FormGroup className={`${validation.replyName ? 'has-error' : ''}`}>
                <h3>
                  Reply To Name&nbsp;
                  <FontAwesomeIcon icon="question-circle" id="replyToNameToolTip" />
                  <UncontrolledTooltip placement="bottom" target="replyToNameToolTip">
                    This is the name your invoice replies are sent to.
                  </UncontrolledTooltip>
                </h3>
                <Input
                  id="replyToName"
                  className=""
                  name="replyToName"
                  value={emailSettings.replyName}
                  onChange={e => handleEmailSettingChange('replyName', e.target.value)}
                />
                {validation.replyName && (
                  <label className="control-label error-message" htmlFor="emailSubject">
                    {validation.replyName}
                  </label>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={5}>
              <FormGroup className={`${validation.replyEmail ? 'has-error' : ''}`}>
                <h3>
                  Reply To Email&nbsp;
                  <FontAwesomeIcon icon="question-circle" id="replyToEmailToolTip" />
                  <UncontrolledTooltip placement="bottom" target="replyToEmailToolTip">
                    This is the email address your invoice replies are sent to.
                  </UncontrolledTooltip>
                </h3>
                <Input
                  id="replyToEmail"
                  className=""
                  name="replyToEmail"
                  value={emailSettings.replyEmail}
                  onChange={e => handleEmailSettingChange('replyEmail', e.target.value)}
                />
                {validation.replyEmail && (
                  <label className="control-label error-message" htmlFor="emailSubject">
                    {validation.replyEmail}
                  </label>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={5}>
              <FormGroup className={`${validation.BCCError ? 'has-error' : ''}`}>
                <h3>
                  BCC&nbsp;
                  <FontAwesomeIcon icon="question-circle" id="blindCopyToolTip" />
                  <UncontrolledTooltip placement="bottom" target="blindCopyToolTip">
                    If you would like a copy of your invoices, enter the email address you would
                    like to receive the copies here.
                  </UncontrolledTooltip>
                </h3>
                <Input
                  id="blindCopy"
                  className=""
                  maxLength="150"
                  name="blindCopy"
                  value={emailSettings.BCC}
                  onChange={e => handleEmailSettingChange('BCC', e.target.value)}
                />
                {validation.BCCError && (
                  <label className="control-label error-message" htmlFor="emailSubject">
                    {validation.BCCError}
                  </label>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={7}>
              <h3> House Address</h3>
              <UncontrolledDropdown className="w-100" setActiveFromChild tabIndex="0">
                <DropdownToggle
                  nav
                  caret
                  color="white"
                  className="text-truncate small-important"
                  tabIndex="0"
                >
                  {emailSettings.selectedAddr && (
                    <DropdownItem key={2} className="w-100 text-clamp-2 small-important">
                      {`${emailSettings.selectedAddr.addressStreet1}${
                        emailSettings.selectedAddr.addressStreet2
                      }, ${emailSettings.selectedAddr.addressCity},${
                        emailSettings.selectedAddr.showStates
                          ? ` ${emailSettings.selectedAddr.stateName},`
                          : ''
                      } ${emailSettings.selectedAddr.countryName}`}
                    </DropdownItem>
                  )}
                </DropdownToggle>
                <DropdownMenu className="full-width-dropdown" tabIndex="0">
                  {emailSettings.HouseAddr &&
                    emailSettings.HouseAddr.map(houseAddr => {
                      const {
                        addressStreet1,
                        addressStreet2,
                        addressCity,
                        showStates,
                        stateName,
                        id,
                        countryName
                      } = houseAddr || {};
                      return (
                        <DropdownItem
                          key={id}
                          className="w-100 text-clamp-2 small-important"
                          onClick={() => handleEmailSettingChange('selectedAddr', houseAddr)}
                          active={id === emailSettings.selectedAddr.id}
                        >
                          {`${addressStreet1}${addressStreet2}, ${addressCity},${
                            showStates ? ` ${stateName},` : ''
                          } ${countryName}`}
                        </DropdownItem>
                      );
                    })}
                </DropdownMenu>
              </UncontrolledDropdown>
            </Col>
          </Row>
          <Row className="mt-5">
            <Col xs={12} className="mb-3">
              <Alert color="primary">
                Additional Charges | Taxes and Fees&nbsp;
                <FontAwesomeIcon icon="question-circle" id="taxFees" />
                <UncontrolledTooltip placement="bottom" target="taxFees">
                  Once invoices have been created for a sale, you cannot edit or add tax types in
                  the invoice template for that sale. Please be sure to enter tax types in your
                  invoice template prior to creating invoices for each auction.
                </UncontrolledTooltip>
              </Alert>
            </Col>

            {!isInvShippingEnabled && <>
              <Col xs={4}>
                <h3>Apply charges per:</h3>
              </Col>
              <Col xs={2}>
                <FormGroup check>
                  <Input
                    type="radio"
                    id="byInvoice"
                    name="applyCharges"
                    onChange={handleBillingType}
                    checked={chargeByInvoice}
                  />
                  <Label check>Invoice</Label>
                </FormGroup>
              </Col>
              <Col xs={2}>
                <FormGroup check>
                  <Input
                    type="radio"
                    id="byLot"
                    name="applyCharges"
                    onChange={handleBillingType}
                    checked={chargeByLot}
                  />
                  <Label check>Lot</Label>
                </FormGroup>
              </Col>
              <Col xs={12} className="mb-3">
                {processorType === 'p' ? (
                  <span>
                    Depending on your local, state, federal, and country tax laws, you may need to
                    include additional taxes to your invoices. United States Sales Tax will be
                    automatically calculated and applied at the lot level.
                  </span>
                ) : (
                  <span>
                    Depending on your Local, State, Federal and Country Tax Law, you may need to
                    include Tax on your outgoing invoices.
                  </span>
                )}
                </Col>
            </>}
            {taxData && (
              <Container className="mt-5" style={{ paddingLeft: 'unset' }}>
                {taxData &&
                  taxData.map((item, index) => (
                    <TaxType
                      handleDeleteTaxType={handleDeleteTaxType}
                      indexValue={index}
                      setTaxData={setTaxData}
                      taxData={taxData}
                      taxItem={item}
                      validation={validation}
                      isPayrix={isPayrix}
                      key={`tax_${index}`}
                    />
                  ))}
              </Container>
            )}

            {processorType === 'p' && (
              <Container>
                <DummyTaxTemplate />
              </Container>
            )}
            <Col xs={3} className="mt-3">
              <Button
                color="link"
                aria-label="create new "
                onClick={() => handleNewTaxChargeType()}
              >
                + Add tax charge type
              </Button>
            </Col>
            {isPayrix && (
              <>
                {feeData && (
                  <Container className="mt-5">
                    {feeData &&
                      feeData.map((item, index) => {
                        let countryError = false;
                        let nameError = false;
                        let rateError = false;
                        let stateError = false;
                        let checkBoxError = false;

                        if (validation.feeErrors) {
                          validation.feeErrors.map(feeError => {
                            if (feeError.errorIndex === index) {
                              countryError = get(feeError, 'errorCountry', false);
                              nameError = get(feeError, 'errorFeeType', false);
                              rateError = get(feeError, 'errorFeeRate', false);
                              stateError = get(feeError, 'errorState', false);
                              checkBoxError = get(feeError, 'checkBoxError', false);
                            }
                          });
                        }
                        return (
                          <FeeType
                            key={index}
                            item={item}
                            indexValue={index}
                            setFeeData={setFeeData}
                            feeData={feeData}
                            nameError={nameError}
                            countryError={countryError}
                            stateError={stateError}
                            rateError={rateError}
                            checkBoxError={checkBoxError}
                            handleDeleteFeeType={handleDeleteFeeType}
                            toggleSaveDisabled={toggleSaveDisabled}
                            isPayrix={processorType === 'p'}
                          />
                        );
                      })}
                  </Container>
                )}
                <Col xs={3} className="mt-3">
                  <Button
                    color="link"
                    aria-label="create new "
                    onClick={handleNewFeeChargeType}
                  >
                    + Add fee charge type
                  </Button>
                </Col>
              </>
            )}
          </Row>
          <Row className="mt-5">
            <Col xs={12} className="mb-3">
              <Alert color="primary">Messages</Alert>
            </Col>
            <Col xs={12}>
              Add customer messages at the bottom of invoices to communicate payment options or
              special shipping notes. Make sure to give the buyer clear payment and shipping
              instructions.
            </Col>
          </Row>
          <Row className="mt-5">
            {paymentProcessingEnabled && (
              <>
                <Col xs={1}>
                  <FontAwesomeIcon icon="check-circle" className="item-icon color-primary ml-2" />

                  <FontAwesomeIcon icon="credit-card" className="item-icon color-primary ml-2" />
                </Col>
                <Col xs={11}>
                  You are enrolled in online payment processing. Eligible bidders may pay you directly
                  via credit card for sales published after your enrollment date.
                </Col>
              </>
            )}
            {processorType !== 'p' && (
              <Col xs={12}>
                <div className="mt-4">
                  <img
                    src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
                    alt="Paypal"
                  />
                  <input
                    type="text"
                    id="paypalEmail"
                    defaultValue={paypalEmailValue}
                    className="ml-4 mr-1"
                    size="30"
                    onChange={e => setPaypalEmailValue(e.target.value)}
                  />
                  <FontAwesomeIcon icon="question-circle" id="paypalField" />
                  <UncontrolledTooltip placement="bottom" target="paypalField">
                    Adding the email address associated with your Paypal account will insert a
                    Paypal link into your bidder invoice emails
                  </UncontrolledTooltip>
                </div>
              </Col>
            )}
            {messageData && (
              <Container className="mt-5">
                {messageData &&
                  messageData.map((item, index) => {
                    let hasSequenceErr = false;
                    if (validation.messageErrors) {
                      validation.messageErrors.map(msgErr => {
                        if (msgErr.sequenceError === item.sequence) {
                          hasSequenceErr = true;
                        }
                      });
                    }
                    return (
                      <Messages
                        key={index}
                        item={item}
                        messageData={messageData}
                        setMessageData={setMessageData}
                        indexValue={index}
                        hasSequenceErr={hasSequenceErr}
                      />
                    );
                  })}
              </Container>
            )}
            <Col xs={6} className="mt-3 text-left">
              <Button color="link" aria-label="create new " onClick={() => handleNewMessage()}>
                + Add another message
              </Button>
            </Col>
            <Col xs={6} className="mt-3 text-right">
              {!readOnlyTemplate && (
                <Button
                  color="primary"
                  aria-label="create new "
                  onClick={handleSaveChanges}
                  disabled={isSavingTemplate || disabledButton}
                  className={disabledButton ? 'disabled-cursor' : ''}
                >
                  Save Changes
                </Button>
              )}
              {validation.genericError && (
                <div className="mt-1">
                  <label className="control-label error-message" htmlFor="emailSubject">
                    {validation.genericError}
                  </label>
                </div>
              )}
              {templateSaved && (
                <div className="control-label mt-1">Your changes have been saved.</div>
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default InvoiceTemplates;
